import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { Observable } from 'rxjs';
import { API_BASE_URL } from '../../core/config/api.config';
import { ModerationInspiration, ModerationRecipe, ModerationReviewMessage } from '../../core/models/recipe.models';
import { ModerationService } from '../../core/services/moderation.service';

@Component({
  selector: 'app-moderation-page',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './moderation-page.component.html',
  styleUrl: './moderation-page.component.scss',
})
export class ModerationPageComponent implements OnInit {
  private readonly moderation = inject(ModerationService);

  activeTab: 'recipes' | 'inspirations' | 'reviews' = 'recipes';
  loading = true;
  pendingActionKey: string | null = null;
  expandedRecipeId: number | null = null;

  recipes: ModerationRecipe[] = [];
  allRecipes: ModerationRecipe[] = [];
  inspirations: ModerationInspiration[] = [];
  allInspirations: ModerationInspiration[] = [];
  reviews: ModerationReviewMessage[] = [];
  allReviews: ModerationReviewMessage[] = [];

  searchQuery = '';
  pageSize = 20;
  currentPage = 0;
  totalPages = 1;
  totalItems = 0;
  errorMessage = '';
  rejectingRecipe: ModerationRecipe | null = null;
  rejectionReasonDraft = '';

  ngOnInit(): void {
    this.reload(0);
  }

  setTab(tab: 'recipes' | 'inspirations' | 'reviews'): void {
    if (this.activeTab === tab) {
      return;
    }
    this.activeTab = tab;
    this.currentPage = 0;
    this.searchQuery = '';
    this.expandedRecipeId = null;
    this.reload(0);
  }

  reload(page = this.currentPage): void {
    this.loading = true;
    this.errorMessage = '';
    this.currentPage = page;
    this.expandedRecipeId = null;

    if (this.activeTab === 'recipes') {
      this.moderation.getRecipes('REVIEW', page, this.pageSize).subscribe({
        next: (response) => {
          this.allRecipes = response.items ?? [];
          this.currentPage = response.currentPage ?? page;
          this.totalPages = Math.max(1, response.totalPages ?? 1);
          this.totalItems = response.total ?? this.allRecipes.length;
          this.applySearch(this.searchQuery);
        },
        error: (error) => {
          this.errorMessage = error?.error?.message ?? 'Impossible de charger les recettes à modérer.';
        },
        complete: () => {
          this.loading = false;
        },
      });
      return;
    }

    if (this.activeTab === 'inspirations') {
      this.moderation.getInspirations('REVIEW', page, this.pageSize).subscribe({
        next: (response) => {
          this.allInspirations = response.items ?? [];
          this.currentPage = response.currentPage ?? page;
          this.totalPages = Math.max(1, response.totalPages ?? 1);
          this.totalItems = response.total ?? this.allInspirations.length;
          this.applySearch(this.searchQuery);
        },
        error: (error) => {
          this.errorMessage = error?.error?.message ?? 'Impossible de charger les inspirations à modérer.';
        },
        complete: () => {
          this.loading = false;
        },
      });
      return;
    }

    this.moderation.getReviewMessages(page, this.pageSize).subscribe({
      next: (response) => {
        this.allReviews = response.items ?? [];
        this.currentPage = response.currentPage ?? page;
        this.totalPages = Math.max(1, response.totalPages ?? 1);
        this.totalItems = response.total ?? this.allReviews.length;
        this.applySearch(this.searchQuery);
      },
      error: (error) => {
        this.errorMessage = error?.error?.message ?? 'Impossible de charger les messages d’avis.';
      },
      complete: () => {
        this.loading = false;
      },
    });
  }

  applySearch(query: string): void {
    this.searchQuery = query;
    const normalized = query.trim().toLowerCase();

    if (this.activeTab === 'recipes') {
      this.recipes = this.allRecipes.filter((recipe) => {
        if (!normalized) {
          return true;
        }
        const haystack = [
          recipe.name,
          recipe.description ?? '',
          recipe.ownerDisplayName ?? '',
          recipe.categoryName ?? '',
        ].join(' ').toLowerCase();
        return haystack.includes(normalized);
      });
      return;
    }

    if (this.activeTab === 'inspirations') {
      this.inspirations = this.allInspirations.filter((inspiration) => {
        if (!normalized) {
          return true;
        }
        const haystack = `${inspiration.title ?? ''} ${inspiration.description ?? ''}`.toLowerCase();
        return haystack.includes(normalized);
      });
      return;
    }

    this.reviews = this.allReviews.filter((review) => {
      if (!normalized) {
        return true;
      }
      const haystack = `${review.authorDisplayName ?? ''} ${review.message ?? ''} ${review.recipeId ?? ''}`.toLowerCase();
      return haystack.includes(normalized);
    });
  }

  previousPage(): void {
    if (this.currentPage <= 0) {
      return;
    }
    this.reload(this.currentPage - 1);
  }

  nextPage(): void {
    if (this.currentPage + 1 >= this.totalPages) {
      return;
    }
    this.reload(this.currentPage + 1);
  }

  decideRecipe(recipe: ModerationRecipe, status: 'APPROVED' | 'DENIED', reason?: string): void {
    const normalizedReason = status === 'DENIED'
      ? reason?.trim()?.slice(0, 500)
      : undefined;
    if (status === 'DENIED' && !normalizedReason) {
      this.errorMessage = 'Le motif du rejet est obligatoire.';
      return;
    }
    this.runAction(
      `recipe:${recipe.id}`,
      this.moderation.updateRecipeStatus(recipe.id, status, normalizedReason),
      () => {
      this.allRecipes = this.allRecipes.filter((item) => item.id !== recipe.id);
      if (this.expandedRecipeId === recipe.id) {
        this.expandedRecipeId = null;
      }
      if (this.rejectingRecipe?.id === recipe.id) {
        this.closeRejectDialog(true);
      }
      this.applySearch(this.searchQuery);
      }
    );
  }

  openRejectDialog(recipe: ModerationRecipe): void {
    if (this.pendingActionKey) {
      return;
    }
    this.errorMessage = '';
    this.rejectingRecipe = recipe;
    this.rejectionReasonDraft = recipe.rejectionReason?.trim() ?? '';
  }

  closeRejectDialog(force = false): void {
    if (!force && this.pendingActionKey) {
      return;
    }
    this.rejectingRecipe = null;
    this.rejectionReasonDraft = '';
  }

  confirmRejectDialog(): void {
    if (!this.rejectingRecipe) {
      return;
    }
    this.decideRecipe(this.rejectingRecipe, 'DENIED', this.rejectionReasonDraft);
  }

  isRejectSubmitting(): boolean {
    return !!this.rejectingRecipe && this.isActionPending(`recipe:${this.rejectingRecipe.id}`);
  }

  decideInspiration(inspiration: ModerationInspiration, status: 'APPROVED' | 'DENIED'): void {
    this.runAction(
      `inspiration:${inspiration.id}`,
      this.moderation.updateInspirationStatus(inspiration.id, status),
      () => {
        this.allInspirations = this.allInspirations.filter((item) => item.id !== inspiration.id);
        this.applySearch(this.searchQuery);
      }
    );
  }

  decideReview(review: ModerationReviewMessage, status: 'APPROVED' | 'DENIED'): void {
    this.runAction(`review:${review.id}`, this.moderation.updateReviewMessageStatus(review.id, status), () => {
      this.allReviews = this.allReviews.filter((item) => item.id !== review.id);
      this.applySearch(this.searchQuery);
    });
  }

  isActionPending(key: string): boolean {
    return this.pendingActionKey === key;
  }

  toggleRecipeDetails(recipeId: number): void {
    this.expandedRecipeId = this.expandedRecipeId === recipeId ? null : recipeId;
  }

  isRecipeExpanded(recipeId: number): boolean {
    return this.expandedRecipeId === recipeId;
  }

  recipeCoverImageUrl(recipe: ModerationRecipe): string | null {
    const rawCover = this.normalizeOptionalString(recipe.coverImage)
      ?? this.normalizeOptionalString(recipe.images?.[0]);

    if (!rawCover) {
      return null;
    }

    if (rawCover.startsWith('http://') || rawCover.startsWith('https://')) {
      return rawCover;
    }

    const normalizedCover = rawCover.replace(/^\/+/, '');
    return `${API_BASE_URL}/storage/files/${normalizedCover}`;
  }

  hasRecipeDetails(recipe: ModerationRecipe): boolean {
    return !!(
      this.normalizeOptionalString(recipe.description)
      || (recipe.ingredients?.length ?? 0) > 0
      || (recipe.steps?.length ?? 0) > 0
      || (recipe.countries?.length ?? 0) > 0
      || recipe.cookingTime != null
      || recipe.numberOfPersons != null
      || recipe.difficulty
      || recipe.cost
      || this.normalizeOptionalString(recipe.categoryName)
      || recipe.categoryId != null
    );
  }

  recipeOwnerLabel(recipe: ModerationRecipe): string {
    return this.normalizeOptionalString(recipe.ownerDisplayName)
      ?? (recipe.ownerId != null ? `#${recipe.ownerId}` : 'Inconnu');
  }

  recipeCategoryLabel(recipe: ModerationRecipe): string | null {
    return this.normalizeOptionalString(recipe.categoryName)
      ?? (recipe.categoryId != null ? `#${recipe.categoryId}` : null);
  }

  sortedRecipeSteps(recipe: ModerationRecipe): Array<NonNullable<ModerationRecipe['steps']>[number]> {
    return [...(recipe.steps ?? [])].sort((left, right) => {
      const leftOrder = left.order ?? Number.MAX_SAFE_INTEGER;
      const rightOrder = right.order ?? Number.MAX_SAFE_INTEGER;
      return leftOrder - rightOrder;
    });
  }

  formatCountryCodes(recipe: ModerationRecipe): string {
    return (recipe.countries ?? [])
      .map((code) => (code ?? '').trim().toUpperCase())
      .filter((code) => code.length > 0)
      .join(', ');
  }

  private runAction(key: string, request: Observable<unknown>, onSuccess: () => void): void {
    if (this.pendingActionKey) {
      return;
    }
    this.pendingActionKey = key;
    this.errorMessage = '';

    request.subscribe({
      next: () => {
        onSuccess();
      },
      error: (error) => {
        this.errorMessage = error?.error?.message ?? 'Mise à jour refusée.';
        this.pendingActionKey = null;
      },
      complete: () => {
        this.pendingActionKey = null;
      },
    });
  }

  private normalizeOptionalString(value: unknown): string | null {
    if (typeof value !== 'string') {
      return null;
    }
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
}
